/**
 * Placement-level CTA analytics.
 *
 * Every primary call to action on a page renders at one of four placements:
 * hero, mid_page, final, or sticky. This helper emits ONE consistently shaped
 * event (`primary_cta_click`) for all of them so conversion can be compared by
 * placement in GA4 / GTM without reverse-engineering per-page event names.
 *
 * It also forwards to `trackCTA` so existing CTA dashboards keep working.
 *
 * Rule: never invent a new placement string in a page component — use
 * `CTA_PLACEMENTS` and pass the CTA object from `src/content/primary-cta.ts`.
 */
import { trackCTA, trackCTAConversion, trackEvent } from "@/lib/tracking";
import type { PrimaryCTA } from "@/content/primary-cta";

export const CTA_PLACEMENTS = ["hero", "mid_page", "final", "sticky"] as const;
export type CTAPlacement = (typeof CTA_PLACEMENTS)[number];

type Extra = Record<string, string | number | boolean | undefined>;

/** Minimal shape a placement CTA needs — `PrimaryCTA` satisfies it. */
export type PlacementCTA = Pick<PrimaryCTA, "id" | "kind" | "label" | "href">;

export function trackPlacementCTA(
  placement: CTAPlacement,
  cta: PlacementCTA,
  extra: Extra = {},
): true {
  const payload = {
    placement,
    cta_id: cta.id,
    cta_kind: cta.kind,
    cta_label: cta.label,
    destination: cta.href,
    ...extra,
  };
  // Placement-comparable event (one name, placement as a dimension).
  trackEvent("primary_cta_click", payload);
  // Remember it so a later form submission can report its originating CTA.
  rememberPlacement(placement, cta.id);
  // Back-compat: existing cta_click dashboards keep receiving the click.
  trackCTA(cta.id, payload);
  // Conversion signal for ad platforms: one name, placement as a dimension.
  trackCTAConversion(`${cta.kind}_cta`, payload);
  // Returns `true` so it can be chained after another tracker in a JSX
  // onClick expression (`trackCTA(...) || trackPlacementCTA(...)`).
  return true;
}

/** Convenience for onClick handlers. */
export const onPlacementCTA =
  (placement: CTAPlacement, cta: PlacementCTA, extra?: Extra) => () =>
    trackPlacementCTA(placement, cta, extra);

/* ------------------------------------------------------------------ */
/* Placement memory — lets a lead form report which CTA sent the user. */
/* ------------------------------------------------------------------ */

const PLACEMENT_KEY = "cta_last_placement";

export type LastPlacement = { placement: CTAPlacement; ctaId: string; ts: number };

/** Remember the most recent tracked CTA click for this session. */
export function rememberPlacement(placement: CTAPlacement, ctaId: string) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      PLACEMENT_KEY,
      JSON.stringify({ placement, ctaId, ts: Date.now() } satisfies LastPlacement),
    );
  } catch {
    /* storage unavailable — placement simply reports as absent */
  }
}

/** Most recent tracked CTA click, or null when the visitor arrived cold. */
export function getLastPlacement(): LastPlacement | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(PLACEMENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LastPlacement;
    return parsed?.placement ? parsed : null;
  } catch {
    return null;
  }
}
